/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { ColorMaps, HeightmapBufferGeometry, Materials, Orientation } from '@sick-davinci/glow';
import { CrownImageType, } from './CrownDesc';
import { CrownCapNames } from './CrownCapNames';
import { Color, Mesh, DoubleSide, FaceColors } from 'three';
function asUint8Array(imageData) {
    const { buffer, byteOffset, byteLength } = imageData.Data.Pixels.data;
    return new Uint8Array(buffer, byteOffset, byteLength);
}
function asUint16Array(imageData) {
    const view = imageData.Data.Pixels.data;
    const len = view.byteLength / 2;
    const arr = new Uint16Array(len);
    const isLittleEndian = imageData.Data.Pixels.endian === 'little';
    for (let i = 0; i < len; i++) {
        arr[i] = view.getUint16(i * 2, isLittleEndian);
    }
    return arr;
}
// tslint:disable:no-bitwise
const UINT8_MAX = (1 << 8) - 1;
const UINT16_MAX = (1 << 16) - 1;
/**
 * Capability to render heightmaps
 */
export class CrownHeightmapCap {
    getName() {
        return CrownCapNames.CROWNHEIGHTMAP;
    }
    doClassToCapabilityMapping(map) {
        map.set(CrownHeightmapCap.className, CrownCapNames.CROWNHEIGHTMAP);
    }
    call(objectIn, sceneIn) {
        const payload = objectIn.payload;
        if (payload.Type === CrownHeightmapCap.className && payload.Iconics.length >= 1) {
            const decoration = !!payload.Decorations && payload.Decorations.length === 1 ? payload.Decorations[0].data : undefined;
            let max = UINT16_MAX;
            let min = 0;
            if (!!decoration) {
                max = decoration.DataRange.Max;
                min = decoration.DataRange.Min;
            }
            const range = max - min;
            const [depthImage, intensityImage] = payload.Iconics;
            let materialDesc = !!objectIn.materials && objectIn.materials.length === 1 ? objectIn.materials[0] : undefined;
            if (!materialDesc || !materialDesc.type) { // no material or only colormap provided?
                materialDesc = {
                    type: 'basic',
                    params: {
                        color: 0xffffff,
                        side: DoubleSide,
                        flatShading: true,
                        vertexColors: FaceColors,
                    },
                    colorMap: (!!materialDesc && materialDesc.colorMap) ? materialDesc.colorMap : undefined,
                };
            }
            const material = Materials.create(materialDesc, sceneIn);
            if (!!depthImage) {
                const { Width, Height, ImageType } = depthImage.data.Data;
                if (ImageType === CrownImageType.UINT16) { // depth image
                    const heightValues = asUint16Array(depthImage.data);
                    let getColor = (idx) => {
                        const c = (heightValues[idx] - min) / range;
                        return new Color(c, c, c);
                    };
                    let colorValues = null;
                    if (!!intensityImage) {
                        const { Width: intensityWidth, Height: intensityHeight, ImageType: intensityImageType } = intensityImage.data.Data;
                        if (intensityImageType === CrownImageType.UINT8) { // intensity image
                            if (intensityHeight === Height && intensityWidth === Width) {
                                colorValues = asUint8Array(intensityImage.data);
                                getColor = (idx) => {
                                    const c = colorValues[idx] / UINT8_MAX;
                                    return new Color(c, c, c);
                                };
                            }
                            else {
                                console.warn('CrownHeightmapCap: intensity-image does not match depth-image dimension!'); // tslint:disable-line:no-console
                                return false;
                            }
                        }
                    }
                    if (!!materialDesc.colorMap) {
                        const model = ColorMaps.getModelByName(objectIn.materials[0].colorMap);
                        getColor = (idx) => {
                            return ColorMaps.getColorAt((heightValues[idx] - min) / range, model);
                        };
                    }
                    if (!this.geometry) {
                        this.geometry = new HeightmapBufferGeometry(Width, Height, heightValues, getColor);
                    }
                    else if (this.geometry.width !== Width && this.geometry.height !== Height) {
                        this.geometry.dispose();
                        this.geometry = new HeightmapBufferGeometry(Width, Height, heightValues, getColor);
                    }
                    else {
                        this.geometry.updateValues(heightValues, getColor);
                    }
                }
            }
            const mesh = new Mesh(this.geometry, material);
            mesh.name = objectIn.name;
            Orientation.set(mesh, objectIn);
            sceneIn.addToParent(objectIn.parent || '', mesh);
            sceneIn.setTag(objectIn.tag, mesh);
            return true;
        }
        return false;
    }
}
CrownHeightmapCap.className = 'HEIGHTMAP';
//# sourceMappingURL=CrownHeightmapCap.js.map